import { useEffect, useState } from "react";
import {
  LogSet as LogSetSchema,
  paginate,
  type LogSet,
  type TimeRange,
} from "@heraion/shared";

import type { ProgressApiFetch } from "./types";

/** Page cap of one window load. Past it the result is flagged `capped`. */
export const WINDOW_SETS_MAX_PAGES = 10;

export type WindowSetsQuery = {
  sinceIso?: string;
  untilIso?: string;
  cursor: string | null;
};

// THE "lazy-fetch the raw LogSets of a displayed interval" hook
// (CN-window-sets): cancel-on-unmount, per-row safeParse (one malformed row
// can't blank a panel) and the cursor walk live here so callsites can't drift.
// since/until come from the caller's `range` (never the clock inside the
// fetch); `capped` is true when the last page fetched still had a cursor.
//
// Per-caller parameters, not forks:
//   • makePath: WHICH route serves the window (/api/sets is self-only; the
//     coach goes through /api/sessions?include=sets). null = nothing to fetch.
//   • extractRaw: where raw sets sit in that route's envelope.
export function useWindowSets(
  apiFetch: ProgressApiFetch,
  range: TimeRange | undefined,
  makePath: (q: WindowSetsQuery) => string | null,
  extractRaw: (resp: unknown) => unknown[],
  key?: string,
): {
  sets: ReadonlyArray<LogSet> | null;
  error: string | null;
  capped: boolean;
} {
  const [sets, setSets] = useState<ReadonlyArray<LogSet> | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [capped, setCapped] = useState(false);
  const fromMs = range?.fromMs;
  const toMs = range?.toMs;
  useEffect(() => {
    const sinceIso =
      fromMs === undefined ? undefined : new Date(fromMs).toISOString();
    const untilIso =
      toMs === undefined ? undefined : new Date(toMs).toISOString();
    setError(null);
    setSets(null);
    setCapped(false);
    if (makePath({ sinceIso, untilIso, cursor: null }) === null) return undefined;
    let cancelled = false;
    void (async () => {
      try {
        const last = { cursor: null as string | null };
        const { items } = await paginate<unknown>(
          async (cursor) => {
            const path = makePath({ sinceIso, untilIso, cursor });
            const resp = await apiFetch<{ cursor?: string | null }>(
              path ?? "",
            );
            return { items: extractRaw(resp), cursor: resp.cursor ?? null };
          },
          {
            maxPages: WINDOW_SETS_MAX_PAGES,
            signal: { get aborted() { return cancelled; } },
            onPage: (page) => {
              last.cursor = page.cursor;
            },
          },
        );
        if (cancelled) return;
        const parsed: LogSet[] = [];
        for (const raw of items) {
          const p = LogSetSchema.safeParse(raw);
          if (p.success) parsed.push(p.data);
        }
        setCapped(last.cursor !== null);
        setSets(parsed);
      } catch (e) {
        if (!cancelled) setError(e instanceof Error ? e.message : "load_failed");
      }
    })();
    return () => {
      cancelled = true;
    };
    // The range edges and `key` are the real inputs: makePath/extractRaw are
    // inline lambdas (new identity every render) and apiFetch is a stable
    // per-surface seam, so widening the deps would refetch on every render.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [fromMs, toMs, key]);
  return { sets, error, capped };
}
